import React, { useState, useRef, useEffect } from 'react';
import Header from '../../../components/layout/headers/Header';
import DynamicNavigation from "../../../components/layout/navigation/DynamicNavigation";
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { BlockUI } from 'primereact/blockui';
import { Toast } from 'primereact/toast';
import { useHistory } from 'react-router-dom';
import { Stepper, Step } from 'react-form-stepper';
import { createClaim, getClaimDocumentTypes, uploadClaimDocument } from '../../../services/claims-service';
import DragDropUploader from '../components/DragDropUploader';
// import '../../../assets/styles/auth-tokens.css';
// import '../../../assets/styles/dashboard-theme.css';

const CreateClaim = () => {
    const history = useHistory();
    const toast = useRef(null);

    const [loading, setLoading] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [referenceNumber, setReferenceNumber] = useState(null);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [currentDocTypeIndex, setCurrentDocTypeIndex] = useState(0);
    const [selectedFile, setSelectedFile] = useState(null);
    
    // Claim Submission Request Payload
    const [formData, setFormData] = useState({
        policyNumber: '',
        claimantFirstName: '',
        claimantLastName: '',
        claimantNationalId: '',
        claimantRelationship: '',
        claimantMobile: '',
        claimantEmail: '',
        deceasedFirstName: '',
        deceasedLastName: '',
        deceasedNationalId: '',
        causeOfDeath: '',
        placeOfDeath: ''
    });

    useEffect(() => {
        fetchDocTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchDocTypes = async () => {
        try {
            const docTypesRes = await getClaimDocumentTypes();
            if (docTypesRes.data?.status === 'SUCCESS') {
                const types = docTypesRes.data.responseBody || [];
                if (types.length > 0) {
                    setDocumentTypes(types);
                } else {
                    toast.current?.show({ severity: 'info', summary: 'Info', detail: 'No documents required.', life: 3000 });
                    setTimeout(() => history.goBack(), 1500);
                }
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch document types.', life: 3000 });
                setTimeout(() => history.goBack(), 1500);
            }
        } catch (error) {
            console.error('Error fetching doc types:', error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch document types.', life: 3000 });
            setTimeout(() => history.goBack(), 1500);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const submitClaimDetails = async (e) => {
        e.preventDefault();
        
        if (!formData.policyNumber.trim() || !formData.claimantFirstName.trim() || !formData.deceasedFirstName.trim()) {
            toast.current.show({ severity: 'error', summary: 'Validation Error', detail: 'Please fill out required fields.', life: 3000 });
            return;
        }

        setLoading(true);

        const payload = {
            policyNumber: formData.policyNumber,
            claimant: {
                firstName: formData.claimantFirstName,
                lastName: formData.claimantLastName,
                nationalId: formData.claimantNationalId,
                relationshipToDeceased: formData.claimantRelationship,
                mobileNumber: formData.claimantMobile,
                email: formData.claimantEmail
            },
            deceased: {
                firstName: formData.deceasedFirstName,
                lastName: formData.deceasedLastName,
                nationalId: formData.deceasedNationalId
            },
            causeOfDeath: formData.causeOfDeath,
            placeOfDeath: formData.placeOfDeath
        };
        try {
            const response = await createClaim(payload);
            
            if (response.data?.status === 'SUCCESS') {
                const refNum = response.data.responseBody;
                setReferenceNumber(refNum);
                toast.current.show({ severity: 'success', summary: 'Success', detail: 'Claim created successfully.', life: 3000 });
                
                if (documentTypes.length > 0) {
                    setActiveStep(1); // Move to documents
                } else {
                    toast.current.show({ severity: 'info', summary: 'Info', detail: 'No documents required.', life: 3000 });
                    setTimeout(() => history.push(`/claims`), 1500);
                }
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: response.data?.message || 'Failed to create claim.', life: 3000 });
            }
        } catch (error) {
            console.error('Error creating claim:', error);
            const errMsg = error.response?.data?.message || 'Failed to submit claim due to network error.';
            toast.current.show({ severity: 'error', summary: 'Error', detail: errMsg, life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (e) => {
        e.preventDefault();
        if (!selectedFile) {
            toast.current.show({ severity: 'error', summary: 'Validation Error', detail: 'Please select a file to upload.', life: 3000 });
            return;
        }

        const docType = documentTypes[currentDocTypeIndex];

        setLoading(true);
        try {
            const response = await uploadClaimDocument(referenceNumber, docType, selectedFile);
            if (response.data?.status === 'SUCCESS') {
                toast.current.show({ severity: 'success', summary: 'Success', detail: `${docType.replace('_', ' ')} uploaded.`, life: 3000 });
                nextDocStep();
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: response.data?.message || 'Failed to upload.', life: 3000 });
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to upload document.', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const nextDocStep = () => {
        setSelectedFile(null); // Reset for next step
        if (currentDocTypeIndex + 1 < documentTypes.length) {
            setCurrentDocTypeIndex(currentDocTypeIndex + 1);
            setActiveStep(1 + currentDocTypeIndex + 1);
        } else {
            // Done
            toast.current.show({ severity: 'success', summary: 'Success', detail: 'All steps completed!', life: 3000 });
            setTimeout(() => {
                history.push('/claims');
            }, 1500);
        }
    };

    const handleSkip = () => {
        nextDocStep();
    };

    const stepStyle = {
        activeBgColor: '#2196F3',
        completedBgColor: '#2196F3'
    };

    return (
        <>
            <Header />
            <DynamicNavigation />
            <Toast ref={toast} />
            <div className="content-body dl-dashboard">
                <div className="container">
                    <BlockUI blocked={loading}>
                        <div className="row justify-content-center">
                            <div className="col-xl-8 col-lg-12">
                                <Card title="Create New Claim">
                                    <Stepper activeStep={activeStep} styleConfig={stepStyle}>
                                        <Step label="Claim Details" />
                                        {documentTypes.map(docType => (
                                            <Step key={docType} label={docType.replace('_', ' ')} />
                                        ))}
                                    </Stepper>

                                    {activeStep === 0 && (
                                        <form onSubmit={submitClaimDetails}>
                                            <div className="p-fluid">
                                                <div className="p-field mb-4">
                                                    <label htmlFor="policyNumber">Policy Number *</label>
                                                    <InputText id="policyNumber" name="policyNumber" value={formData.policyNumber} onChange={handleInputChange} />
                                                </div>
                                                <h5 className="mt-4 mb-3">Claimant Details</h5>
                                                <div className="p-field mb-3">
                                                    <label htmlFor="claimantFirstName">First Name *</label>
                                                    <InputText id="claimantFirstName" name="claimantFirstName" value={formData.claimantFirstName} onChange={handleInputChange} />
                                                </div>
                                                <div className="p-field mb-3">
                                                    <label htmlFor="claimantLastName">Last Name *</label>
                                                    <InputText id="claimantLastName" name="claimantLastName" value={formData.claimantLastName} onChange={handleInputChange} />
                                                </div>
                                                <div className="p-field mb-3">
                                                    <label htmlFor="claimantNationalId">National ID</label>
                                                    <InputText id="claimantNationalId" name="claimantNationalId" value={formData.claimantNationalId} onChange={handleInputChange} />
                                                </div>
                                                <div className="p-field mb-3">
                                                    <label htmlFor="claimantRelationship">Relationship to Deceased</label>
                                                    <InputText id="claimantRelationship" name="claimantRelationship" value={formData.claimantRelationship} onChange={handleInputChange} />
                                                </div>
                                                <div className="p-field mb-3">
                                                    <label htmlFor="claimantMobile">Mobile Number</label>
                                                    <InputText id="claimantMobile" name="claimantMobile" value={formData.claimantMobile} onChange={handleInputChange} />
                                                </div>
                                                <div className="p-field mb-3">
                                                    <label htmlFor="claimantEmail">Email</label>
                                                    <InputText id="claimantEmail" name="claimantEmail" value={formData.claimantEmail} onChange={handleInputChange} />
                                                </div>
                                                <h5 className="mt-4 mb-3">Deceased Details</h5>
                                                <div className="p-field mb-3">
                                                    <label htmlFor="deceasedFirstName">First Name *</label>
                                                    <InputText id="deceasedFirstName" name="deceasedFirstName" value={formData.deceasedFirstName} onChange={handleInputChange} />
                                                </div>
                                                <div className="p-field mb-3">
                                                    <label htmlFor="deceasedLastName">Last Name *</label>
                                                    <InputText id="deceasedLastName" name="deceasedLastName" value={formData.deceasedLastName} onChange={handleInputChange} />
                                                </div>
                                                <div className="p-field mb-3">
                                                    <label htmlFor="deceasedNationalId">National ID</label>
                                                    <InputText id="deceasedNationalId" name="deceasedNationalId" value={formData.deceasedNationalId} onChange={handleInputChange} />
                                                </div>
                                                <div className="p-field mb-3">
                                                    <label htmlFor="causeOfDeath">Cause of Death</label>
                                                    <InputText id="causeOfDeath" name="causeOfDeath" value={formData.causeOfDeath} onChange={handleInputChange} />
                                                </div>
                                                <div className="p-field mb-3">
                                                    <label htmlFor="placeOfDeath">Place of Death</label>
                                                    <InputText id="placeOfDeath" name="placeOfDeath" value={formData.placeOfDeath} onChange={handleInputChange} />
                                                </div>
                                            </div>
                                            <div className="d-flex justify-content-between mt-4">
                                                <Button type="button" label="Back" icon="pi pi-arrow-left" className="btn btn-secondary p-button-secondary" onClick={() => history.push("/claims")} style={{ width: 'auto' }} />
                                                <Button type="submit" label="Submit and Proceed" icon="pi pi-arrow-right" iconPos="right" className="btn btn-primary text-white p-button-primary" style={{ width: 'auto' }} />
                                            </div>
                                        </form>
                                    )}

                                    {activeStep > 0 && activeStep <= documentTypes.length && (
                                        <div>
                                            <div className="p-fluid">
                                                <DragDropUploader 
                                                    label={`Upload Document: ${documentTypes[currentDocTypeIndex].replace('_', ' ')}`}
                                                    accept="image/*,.pdf"
                                                    maxFileSize={5000000}
                                                    onFileSelect={(file) => setSelectedFile(file)}
                                                />
                                            </div>
                                            <div className="d-flex justify-content-between mt-4">
                                                <Button type="button" label="Skip" icon="pi pi-step-forward" className="btn btn-secondary p-button-secondary" onClick={handleSkip} style={{ width: 'auto' }} />
                                                <Button type="button" label="Upload and Proceed" icon="pi pi-upload" className="btn btn-primary text-white p-button-primary" onClick={handleFileUpload} style={{ width: 'auto' }} disabled={!selectedFile} />
                                            </div>
                                        </div>
                                    )}
                                </Card>
                            </div>
                        </div>
                    </BlockUI>
                </div>
            </div>
        </>
    );
};

export default CreateClaim;
